import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  api,
  type Me,
  type Membership,
  type OrgContext,
  type OrgUnit,
  type Person,
} from '../lib/api';
import { isSignedIn, signOut as cognitoSignOut } from './cognito';

// Session state: whether someone is signed in, who they are (/v1/me, including MFA status), and
// which org context they're working in (remembered per user).

type Status = 'checking' | 'signedOut' | 'signedIn';

interface SessionValue {
  status: Status;
  markSignedIn: () => void;
  signOut: () => Promise<void>;
  org: OrgContext | null;
  membership: Membership | null;
  setOrg: (org: OrgContext) => void;
}

const SessionContext = createContext<SessionValue | null>(null);
const orgKey = (userId: string) => `clairo.org.${userId}`;

export function SessionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<Status>('checking');
  const [org, setOrgState] = useState<OrgContext | null>(null);

  useEffect(() => {
    void isSignedIn().then((yes) => setStatus(yes ? 'signedIn' : 'signedOut'));
  }, []);

  const me = useMe(status === 'signedIn');

  // Pick the remembered org context, or the first membership.
  useEffect(() => {
    if (!me.data) return;
    const memberships = me.data.memberships;
    let saved: OrgContext | null = null;
    try {
      saved = JSON.parse(
        localStorage.getItem(orgKey(me.data.user.id)) ?? 'null',
      ) as OrgContext | null;
    } catch {
      saved = null;
    }
    const valid = memberships.find(
      (m) => m.tenant.id === saved?.tenantId && m.orgUnit.id === saved?.orgUnitId,
    );
    const first = memberships[0];
    setOrgState(
      valid ? saved : first ? { tenantId: first.tenant.id, orgUnitId: first.orgUnit.id } : null,
    );
  }, [me.data]);

  const setOrg = useCallback(
    (next: OrgContext) => {
      setOrgState(next);
      if (me.data) localStorage.setItem(orgKey(me.data.user.id), JSON.stringify(next));
      void queryClient.invalidateQueries({ queryKey: ['org'] });
    },
    [me.data, queryClient],
  );

  const signOut = useCallback(async () => {
    await cognitoSignOut();
    queryClient.clear();
    setOrgState(null);
    setStatus('signedOut');
  }, [queryClient]);

  const membership =
    me.data?.memberships.find(
      (m) => m.tenant.id === org?.tenantId && m.orgUnit.id === org?.orgUnitId,
    ) ?? null;

  const value = useMemo(
    () => ({ status, markSignedIn: () => setStatus('signedIn'), signOut, org, membership, setOrg }),
    [status, signOut, org, membership, setOrg],
  );
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession outside SessionProvider');
  return ctx;
}

// ── Queries ────────────────────────────────────────────────────────────────────────────────

export function useMe(enabled = true) {
  return useQuery({
    queryKey: ['me'],
    queryFn: () => api<Me>('/v1/me'),
    enabled,
    staleTime: 60_000,
  });
}

export function useOrgUnits(org: OrgContext | null) {
  return useQuery({
    queryKey: ['org', org?.tenantId, org?.orgUnitId, 'units'],
    queryFn: () => api<{ items: OrgUnit[] }>('/v1/org-units', { org }),
    enabled: Boolean(org),
    select: (data) => data.items,
  });
}

export interface NewOrgUnit {
  parentId: string;
  type: string;
  name: string;
  isCustodian: boolean;
  province?: string;
}

export function useCreateOrgUnit(org: OrgContext | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: NewOrgUnit) => api<OrgUnit>('/v1/org-units', { method: 'POST', body, org }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['org'] }),
  });
}

export function usePeople(org: OrgContext | null) {
  return useQuery({
    queryKey: ['org', org?.tenantId, org?.orgUnitId, 'people'],
    queryFn: () => api<{ items: Person[] }>('/v1/users', { org }),
    enabled: Boolean(org),
    select: (data) => data.items,
  });
}

export type PersonAction = 'unlock' | 'signOut' | 'remove';

/** Admin actions on a person (§9b); refreshes the list afterwards. */
export function usePersonAction(org: OrgContext | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ action, userId }: { action: PersonAction; userId: string }) => {
      if (action === 'unlock') return api(`/v1/users/${userId}/unlock`, { method: 'POST', org });
      if (action === 'signOut')
        return api(`/v1/users/${userId}/sign-out-everywhere`, { method: 'POST', org });
      return api(`/v1/users/${userId}/membership`, { method: 'DELETE', org });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['org'] }),
  });
}
